import API from 'services/API';

// On-demand preview-generatie (sides die bij upload zijn overgeslagen via
// options.onUpload.generatePreviewForSides) laat de server per side een mutool/ghostscript-proces
// starten. De worker stuurt zulke verzoeken bewust rechtstreeks door, buiten de PREVIEW_QUEUE om.
// Zonder begrenzing aan de clientkant vuurt een grid met tientallen zichtbare pagina's dus
// tientallen gelijktijdige verzoeken af, waarop de server (Windows) geen processen meer kan
// spawnen ("The system cannot execute the specified program" / "spawn UNKNOWN") en crasht.
// Deze queue houdt het aantal gelijktijdige verzoeken klein en laat wachtende verzoeken
// annuleren als de pagina alweer uit beeld is.
const MAX_CONCURRENT_REQUESTS = 3;

type Job = {
  sessionId: string;
  sideId: string;
  resolve: () => void;
  reject: (error: unknown) => void;
};

const pending: Job[] = [];
const active = new Set<string>();

const pump = (): void => {
  while (active.size < MAX_CONCURRENT_REQUESTS && pending.length > 0) {
    const job = pending.shift() as Job;
    active.add(job.sideId);
    API.generateSidePreview(job.sessionId, job.sideId)
      .then(() => job.resolve())
      .catch((error) => job.reject(error))
      .finally(() => {
        active.delete(job.sideId);
        pump();
      });
  }
};

export type EnqueueOptions = {
  // 'high': vooraan in de wachtrij (de pagina die de gebruiker zojuist heeft geopend hoort niet
  // te wachten achter grid-cellen die toevallig eerder in beeld kwamen).
  priority?: 'normal' | 'high';
};

// Zet een preview-verzoek in de wachtrij. De promise resolvet zodra de worker het verzoek heeft
// geaccepteerd (de preview zelf komt later via de websocket) en rejectet bij een fout of als het
// verzoek geannuleerd is voordat het gestart was (zie isCancelledSidePreviewError).
export const enqueueSidePreviewGeneration = (
  sessionId: string,
  sideId: string,
  { priority = 'normal' }: EnqueueOptions = {},
): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const job: Job = { sessionId, sideId, resolve, reject };
    if (priority === 'high') pending.unshift(job);
    else pending.push(job);
    pump();
  });

export class SidePreviewCancelledError extends Error {
  constructor(sideId: string) {
    super(`Side preview request for ${sideId} was cancelled before it started`);
    this.name = 'SidePreviewCancelledError';
  }
}

export const isCancelledSidePreviewError = (error: unknown): boolean =>
  error instanceof SidePreviewCancelledError;

// Haalt een nog niet gestart verzoek uit de wachtrij (bv. omdat de pagina uit beeld scrolde).
// Een verzoek dat al onderweg is, wordt niet afgebroken. Geeft terug of er iets geannuleerd is.
export const cancelQueuedSidePreview = (sideId: string): boolean => {
  const index = pending.findIndex((job) => job.sideId === sideId);
  if (index === -1) return false;
  const [job] = pending.splice(index, 1);
  job.reject(new SidePreviewCancelledError(sideId));
  return true;
};

// Alleen voor tests / debugging.
export const getSidePreviewQueueState = (): { pending: number; active: number } => ({
  pending: pending.length,
  active: active.size,
});
